/**
 * Login Page Component
 * Split screen design with MEV-Med branding and authentication form
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';

import { useAuth } from '../stores/authStore';
import branding from '../config/branding';
import BrandLogo from '../components/common/BrandLogo';
import { authService } from '../services/authService';
import { LoginCredentials } from '../types/auth';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError, mfaPending } = useAuth();

  // Form state - initialize with remembered email if available
  const [formData, setFormData] = useState<LoginCredentials>(() => {
    if (authService.getRememberMe()) {
      const rememberedEmail = authService.getRememberedEmail();
      if (rememberedEmail) {
        return {
          email: rememberedEmail,
          password: '',
          rememberMe: true,
        };
      }
    }
    return {
      email: '',
      password: '',
      rememberMe: false,
    };
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Get redirect path from location state
  const from = (location.state as any)?.from?.pathname || '/home';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Redirect to MFA verify page when MFA is pending
  useEffect(() => {
    if (mfaPending) {
      navigate('/mfa-verify', { replace: true, state: { from: { pathname: from } } });
    }
  }, [mfaPending, navigate, from]);

  // Clear error when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [formData, clearError]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'E-mail é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'E-mail inválido';
    }

    if (!formData.password) {
      errors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(formData);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleInputChange = (field: keyof LoginCredentials) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'rememberMe' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', width: '100%' }}>
      <Grid container sx={{ minHeight: '100vh', flex: 1 }}>
        {/* Left Side - Branding */}
        <Grid
          sx={{
            background: `linear-gradient(135deg, ${branding.colors.loginGradientStart} 0%, ${branding.colors.loginGradientEnd} 100%)`,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            },
          }}
          size={{ md: 6 }}>
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', color: 'white', zIndex: 1, px: 4 }}>
              <BrandLogo
                variant="light"
                sx={{
                  maxWidth: { md: 280, lg: 340 },
                  height: 'auto',
                  mb: 3,
                  filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.3))',
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 400,
                  opacity: 0.95,
                  textShadow: '0 1px 5px rgba(0,0,0,0.2)',
                }}
              >
                {branding.appDescription}
              </Typography>
            </Box>
          </Fade>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: { xs: 2, sm: 4 },
          }}
          size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              width: '100%',
              maxWidth: 400,
              border: { xs: 'none', md: '1px solid' },
              borderColor: { xs: 'transparent', md: 'divider' },
              borderRadius: 3,
              backgroundColor: { xs: 'transparent', md: 'background.paper' },
            }}
          >
            {/* Mobile Logo */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                textAlign: 'center',
                mb: 4,
              }}
            >
              <BrandLogo
                variant="dark"
                sx={{ maxWidth: 200, height: 'auto', mb: 1 }}
              />
              <Typography variant="body1" color="text.secondary">
                {branding.appDescription}
              </Typography>
            </Box>

            {/* Form Header */}
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}
            >
              Entrar
            </Typography>

            {/* Error Alert */}
            {error && (
              <Fade in>
                <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
                  {error}
                </Alert>
              </Fade>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                id="email"
                name="email"
                type="email"
                label="E-mail"
                value={formData.email}
                onChange={handleInputChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={isLoading}
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Senha"
                value={formData.password}
                onChange={handleInputChange('password')}
                error={!!formErrors.password}
                helperText={formErrors.password}
                disabled={isLoading}
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.rememberMe}
                      onChange={handleInputChange('rememberMe')}
                      disabled={isLoading}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Lembrar-me</Typography>}
                />
                <Link
                  to="/forgot-password"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ '&:hover': { textDecoration: 'underline' } }}
                  >
                    Esqueci minha senha
                  </Typography>
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Entrar'
                )}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPage;
